import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ItineraryImportError, requestItineraryImport } from '@/api/itineraryImport';
import ResponsiveOverlay from '@/components/ResponsiveOverlay';
import '@/components/TripImport/TripImport.css';
import {
  TripImportInputStep,
  type OutputFormatPreference,
} from '@/components/TripImport/TripImportInputStep';
import { TripImportSocialInputStep } from '@/components/TripImport/TripImportSocialInputStep';
import { TripImportSourcePreviewPanel } from '@/components/TripImport/TripImportSourcePreviewPanel';
import { TripImportProgressStep } from '@/components/TripImport/TripImportProgressStep';
import { TripImportReviewStep } from '@/components/TripImport/TripImportReviewStep';
import { TripImportDiscardConfirmDialog } from '@/components/TripImport/TripImportDiscardConfirmDialog';
import { TripImportSuccessStep } from '@/components/TripImport/TripImportSuccessStep';
import type { InspoImportSaveSummary } from '@/components/InspoImport/inspoImportTypes';
import SaveDestinationPickerPanel, {
  type SaveDestinationSelection,
} from '@/components/SaveToOverlay/SaveDestinationPickerPanel';
import { getUserItineraries, getUserLists, updatePostPrivacy, type SavedItinerary } from '@/api/itinerary';
import { usePostShare } from '@/hooks/usePostShare';
import { useAuth } from '@/contexts';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import { clientUploadImportDocument } from '@/lib/upload/clientUpload';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_TRIP_IMPORT_CREATE_FAILED,
  EVENT_TRIP_IMPORT_ADD_TO_EXISTING_CLICKED,
  EVENT_TRIP_IMPORT_CREATE_STARTED,
  EVENT_TRIP_IMPORT_CREATE_SUCCEEDED,
  EVENT_TRIP_IMPORT_DESTINATION_SELECTED,
  EVENT_TRIP_IMPORT_FLOW_CANCELLED,
  EVENT_TRIP_IMPORT_FLOW_OPENED,
  EVENT_TRIP_IMPORT_PARSE_FAILED,
  EVENT_TRIP_IMPORT_PARSE_STARTED,
  EVENT_TRIP_IMPORT_PARSE_SUCCEEDED,
  EVENT_TRIP_IMPORT_PLACE_DETAILS_TOGGLED,
  EVENT_TRIP_IMPORT_PLACE_MATCHED,
  EVENT_TRIP_IMPORT_PLACE_TOGGLED,
  EVENT_CUSTOM_PLACE_UPGRADED,
  EVENT_TRIP_IMPORT_RESULT_TYPE_SWITCHED,
  EVENT_TRIP_IMPORT_UPLOAD_FAILED,
  EVENT_TRIP_IMPORT_UPLOAD_STARTED,
  EVENT_TRIP_IMPORT_VIEW_LIST_CLICKED,
  EVENT_TRIP_IMPORT_VIEW_TRIP_CLICKED,
  EVENT_TRIP_IMPORT_KEEP_EDITING_CLICKED,
  EVENT_TRIP_IMPORT_PUBLISH_CLICKED,
  EVENT_TRIP_IMPORT_PUBLISHED,
  EVENT_TRIP_IMPORT_PUBLISH_FAILED,
} from '@/lib/analyticsEvents';
import {
  IMPORT_PDF_MAX_PAGES,
  IMPORT_TEXT_MAX_CHARS,
  type ItineraryImportDraft,
  isSocialItineraryImportSourceType,
  type ItineraryImportSourceType,
  type TripImportEntry,
  type TripImportResultType,
  resolveSuggestedResultType,
} from '@/models/itineraryImportTypes';
import { createListWithPlace } from '@/api/itinerary';
import {
  addSocialImportPlacesToDestination,
  bulkSaveSocialImportPlaces,
  type SocialImportDestination,
} from '@/utils/inspoImportBuilder';
import { invalidateExistingPostCache } from '@/utils/itineraryExistingPostCache';
import {
  buildImportSourceMetadata,
  countDraftPlaces,
  countTravelRowsInDraft,
  createItineraryFromImportDraft,
  defaultSelectedPlaceIds,
  flattenDraftToListPlaces,
  formatRedactionSummary,
  importListNotesFromDraft,
  listCreateDestinationOptionsFromDraft,
  summarizeImport,
  resolveImportDraftDestination,
} from '@/utils/itineraryImportBuilder';
import { applyGoogleMatchToImportPlace } from '@/utils/customPlaceUpgrade';
import PlaceSearchAutocomplete from '@/components/PlaceSearchAutocomplete/PlaceSearchAutocomplete';
import type Location from '@/models/location';
import { buildImportAppendToExistingOptions } from '@/utils/itineraryImportAppend';
import { userMessageForItineraryImportCode } from '@/utils/itineraryImportErrors';
import { pickDominantCountry } from '@/utils/pickDominantCountry';
import { pickDominantState } from '@/utils/pickDominantState';
import { countryToRouteParam, stateToRouteParam, US_COUNTRY_NAME } from '@/components/MyTravel/myTravelTypes';
import { dispatchSavedContentRefresh } from '@/lib/savedContentRefreshEvents';
import { getErrorMessage } from '@/utils/errorHelpers';
import { isCreatorOrAdvisor } from '@/utils/isCreatorOrAdvisor';
import { error as logError, warn as logWarn } from '@/utils/logger';
import {
  buildSocialItineraryImportSource,
  formatSocialImportReviewSubtitle,
} from '@/utils/socialItineraryImport';
import { mongoObjectIdStringOrNull } from '@/utils/idGeneration';
import {
  buildTripImportReviewSections,
  formatTripImportReviewMetaLine,
  countDraftPlacesTotal,
} from '@/utils/tripImportReviewPlaces';
import {
  filterTripImportReviewSections,
  type TripImportReviewFilter,
} from '@/utils/tripImportReviewFilters';
import {
  animateTripImportProgressToComplete,
  flattenDraftPlacePreviews,
  initialTripImportProgressState,
  tripImportProgressReducer,
  type TripImportProgressCopy,
  type TripImportProgressState,
  type TripImportFoundPlacePreview,
} from '@/utils/tripImportProgressReducer';
import type { InspoImportEntrySource } from '@/contexts/InspoImportContext';

type ViewState =
  | 'idle'
  | 'uploading'
  | 'parsing'
  | 'review'
  | 'pick_destination'
  | 'saving'
  | 'save_success'
  | 'error';

type TripImportSavingKind = 'trip' | 'saves' | 'list_destination';

export interface TripImportFlowProps {
  isModal?: boolean;
  variant?: 'modal' | 'onboarding';
  entry?: TripImportEntry;
  prefilledText?: string;
  /** Instagram or TikTok URL — uses authed itinerary import (same draft as PDF/text). */
  prefilledSocialUrl?: string;
  /** When true, skip paste/file input (Inspo / share sheet). */
  socialOnly?: boolean;
  clearPrefilledText?: () => void;
  autoStartImport?: boolean;
  onRequestClose?: () => void;
  onSaveComplete?: () => void;
  onCompleteSave?: (summary: InspoImportSaveSummary) => void;
  onCancel?: () => void;
  entrySource?: InspoImportEntrySource;
  /** Page route — persist accepted URL in query string. */
  onSocialUrlAccepted?: (url: string) => void;
}

function progressCopyForSource(
  socialOnlyFlow: boolean,
  sourceType: ItineraryImportSourceType,
): TripImportProgressCopy {
  if (socialOnlyFlow || isSocialItineraryImportSourceType(sourceType)) {
    return 'social';
  }
  return 'document';
}

function createImportSession(): { importId: string; importStartedAt: string } {
  const importId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `import-${Date.now()}`;
  return { importId, importStartedAt: new Date().toISOString() };
}

export function TripImportFlow({
  isModal = false,
  variant = 'modal',
  entry = 'create_flyout',
  prefilledText = '',
  prefilledSocialUrl = '',
  socialOnly = false,
  clearPrefilledText,
  autoStartImport = false,
  onRequestClose,
  onSaveComplete,
  onCompleteSave,
  onCancel,
  entrySource = 'in_app',
  onSocialUrlAccepted,
}: TripImportFlowProps) {
  const navigate = useNavigate();
  const isMobileViewport = useIsMobileViewport();
  const isOnboardingEmbed = variant === 'onboarding';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const processedTextRef = useRef<string | null>(null);
  const processedSocialUrlRef = useRef<string | null>(null);
  const lastParseRef = useRef<{
    source: Parameters<typeof requestItineraryImport>[0]['source'];
    sourceType: ItineraryImportSourceType;
    session: { importId: string; importStartedAt: string };
  } | null>(null);
  const importAbortRef = useRef<AbortController | null>(null);
  const progressStartedRef = useRef<number | null>(null);
  const progressLatestRef = useRef<TripImportProgressState>(initialTripImportProgressState());
  const progressFrozenRef = useRef(false);
  const progressCopyRef = useRef<TripImportProgressCopy>('document');
  const importShareModalOpenRef = useRef(false);
  const { user } = useAuth();

  const [viewState, setViewState] = useState<ViewState>(() => {
    if (socialOnly && prefilledSocialUrl.trim() && autoStartImport) {
      return 'parsing';
    }
    if (isOnboardingEmbed && prefilledText.trim()) return 'parsing';
    return 'idle';
  });
  const [inputText, setInputText] = useState(prefilledText);
  const [draft, setDraft] = useState<ItineraryImportDraft | null>(null);
  const [placeLookupClientId, setPlaceLookupClientId] = useState<string | null>(null);
  const [sourceType, setSourceType] = useState<ItineraryImportSourceType>('text');
  const [importId, setImportId] = useState('');
  const [importStartedAt, setImportStartedAt] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [resultType, setResultType] = useState<TripImportResultType>('itinerary');
  const [suggestedType, setSuggestedType] = useState<TripImportResultType>('itinerary');
  const [suggestionReason, setSuggestionReason] = useState<string | null>(null);
  const [parseErrorMessage, setParseErrorMessage] = useState('');
  const [savingKind, setSavingKind] = useState<TripImportSavingKind>('trip');
  const [importTitle, setImportTitle] = useState('');
  const [importError, setImportError] = useState<ItineraryImportError | null>(null);
  const [savedTripId, setSavedTripId] = useState<string | null>(null);
  const [savedTripTitle, setSavedTripTitle] = useState('');

  const exitImportToSavedTrip = useCallback(() => {
    if (!savedTripId) return;
    onRequestClose?.();
    navigate(`/itinerary/${savedTripId}`);
  }, [navigate, onRequestClose, savedTripId]);

  const { handleShareClick, shareModal } = usePostShare({
    entry: 'post_publish',
    onShareModalOpen: () => {
      importShareModalOpenRef.current = true;
    },
    onShareModalClose: () => {
      importShareModalOpenRef.current = false;
      exitImportToSavedTrip();
    },
  });
  const [saveSummary, setSaveSummary] = useState<{
    destination: SocialImportDestination | { type: 'saves'; name: string | null; stateName?: string | null };
    savedCount: number;
  } | null>(null);
  const [userItineraries, setUserItineraries] = useState<SavedItinerary[]>([]);
  const [userLists, setUserLists] = useState<SavedItinerary[]>([]);
  const [isLoadingItineraries, setIsLoadingItineraries] = useState(false);
  const [isLoadingLists, setIsLoadingLists] = useState(false);
  const [outputFormatPreference, setOutputFormatPreference] = useState<OutputFormatPreference>('auto');
  const [reviewFilter, setReviewFilter] = useState<TripImportReviewFilter>('all');
  const [reviewDetailed, setReviewDetailed] = useState(false);
  const [progressState, setProgressState] = useState<TripImportProgressState>(() =>
    initialTripImportProgressState(),
  );

  useEffect(() => {
    progressLatestRef.current = progressState;
  }, [progressState]);
  const [progressFoundPlaces, setProgressFoundPlaces] = useState<TripImportFoundPlacePreview[] | null>(
    null,
  );
  const [isPublishing, setIsPublishing] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  useEffect(() => {
    WebAnalytics.track(EVENT_TRIP_IMPORT_FLOW_OPENED, { entry, source_type: 'text' });
  }, [entry]);

  useEffect(() => {
    if (prefilledText.trim()) {
      setInputText(prefilledText);
    }
  }, [prefilledText]);

  const isBusy = viewState === 'uploading' || viewState === 'parsing' || viewState === 'saving';

  const applyDraft = useCallback(
    (nextDraft: ItineraryImportDraft, nextSourceType: ItineraryImportSourceType) => {
      const suggestion = resolveSuggestedResultType(nextDraft);
      setDraft(nextDraft);
      setSourceType(nextSourceType);
      setSelectedIds(defaultSelectedPlaceIds(nextDraft));
      setSuggestedType(suggestion.type);
      setSuggestionReason(suggestion.reason);
      setResultType(
        outputFormatPreference === 'auto' ? suggestion.type : outputFormatPreference,
      );
      setImportTitle(nextDraft.title?.trim() || 'Imported trip');
      setParseErrorMessage('');
      setReviewFilter('all');
      setProgressFoundPlaces(null);
      setViewState('review');
    },
    [outputFormatPreference],
  );

  const runParse = useCallback(
    async (params: {
      source: Parameters<typeof requestItineraryImport>[0]['source'];
      sourceType: ItineraryImportSourceType;
      session: { importId: string; importStartedAt: string };
      force?: boolean;
      /** Keep upload-phase timer/checklist when parse starts (file import). */
      preserveProgressTimer?: boolean;
    }) => {
      lastParseRef.current = {
        source: params.source,
        sourceType: params.sourceType,
        session: params.session,
      };
      importAbortRef.current?.abort();
      const abortController = new AbortController();
      importAbortRef.current = abortController;
      progressFrozenRef.current = false;
      const progressCopy = progressCopyForSource(socialOnly, params.sourceType);
      progressCopyRef.current = progressCopy;
      if (!params.preserveProgressTimer) {
        setProgressState(initialTripImportProgressState(progressCopy));
        progressLatestRef.current = initialTripImportProgressState(progressCopy);
        progressStartedRef.current = Date.now();
      } else if (progressStartedRef.current == null) {
        progressStartedRef.current = Date.now();
      }
      setProgressFoundPlaces(null);
      setViewState('parsing');
      setImportError(null);
      setParseErrorMessage('');
      WebAnalytics.track(EVENT_TRIP_IMPORT_PARSE_STARTED, {
        entry,
        source_type: params.sourceType,
        import_id: params.session.importId,
      });
      const parseStarted = Date.now();
      try {
        const response = await requestItineraryImport({
          source: params.source,
          import_id: params.session.importId,
          import_started_at: params.session.importStartedAt,
          force: params.force ?? false,
          signal: abortController.signal,
        });
        if (countDraftPlaces(response) === 0) {
          const message = isSocialItineraryImportSourceType(params.sourceType)
            ? 'No places found in this post. Try a link with places in the caption.'
            : 'No places found in your text. Try adding city names or more detail.';
          WebAnalytics.track(EVENT_TRIP_IMPORT_PARSE_FAILED, {
            entry,
            source_type: params.sourceType,
            import_id: params.session.importId,
            failure_code: 'NO_PLACES',
            retryable: true,
          });
          setParseErrorMessage(message);
          setViewState('idle');
          return;
        }

        const stats = summarizeImport(response, defaultSelectedPlaceIds(response));
        const socialPostIdPresent = isSocialItineraryImportSourceType(params.sourceType)
          ? Boolean(mongoObjectIdStringOrNull(response.social?.post_id))
          : undefined;
        if (isSocialItineraryImportSourceType(params.sourceType) && !socialPostIdPresent) {
          logWarn('[TripImportFlow] Social import draft missing social.post_id (Mongo)', {
            import_id: params.session.importId,
            platform: response.social?.platform,
            short_code: response.social?.short_code,
          });
          toast.warn(
            'This import could not be linked to the original post for attribution. Your places will still save.',
            { toastId: `social-attribution-${params.session.importId}` },
          );
        }
        WebAnalytics.track(EVENT_TRIP_IMPORT_PARSE_SUCCEEDED, {
          entry,
          source_type: params.sourceType,
          import_id: params.session.importId,
          day_count: stats.dayCount,
          place_count: stats.placeCount,
          bookable_count: stats.bookableCount,
          low_confidence_count: stats.lowConfidenceCount,
          unmatched_count: stats.unmatchedCount,
          redaction_count: stats.redactionCount,
          has_dates: stats.hasDates,
          from_cache: response.from_cache ?? false,
          ...(response.prompt_version ? { prompt_version: response.prompt_version } : {}),
          parse_ms: Date.now() - parseStarted,
          suggested_result_type: resolveSuggestedResultType(response).type,
          travel_count: countTravelRowsInDraft(response),
          ...(socialPostIdPresent !== undefined
            ? { social_post_id_present: socialPostIdPresent }
            : {}),
          progress_visual: progressCopy === 'social' ? 'social_scan' : 'checklist',
          ...(isSocialItineraryImportSourceType(params.sourceType)
            ? {
                has_source_preview: Boolean(
                  typeof response.social?.display_url === 'string' &&
                    response.social.display_url.trim(),
                ),
              }
            : {}),
        });
        setProgressFoundPlaces(flattenDraftPlacePreviews(response.days));
        progressFrozenRef.current = true;
        await animateTripImportProgressToComplete(
          setProgressState,
          progressLatestRef.current,
          progressCopy,
        );
        if (abortController.signal.aborted || importAbortRef.current !== abortController) {
          progressFrozenRef.current = false;
          return;
        }
        progressFrozenRef.current = false;
        applyDraft(response, params.sourceType);
      } catch (err) {
        if (
          abortController.signal.aborted ||
          (err instanceof ItineraryImportError && err.name === 'AbortError') ||
          (err instanceof Error && err.name === 'AbortError')
        ) {
          setViewState('idle');
          return;
        }
        const importErr =
          err instanceof ItineraryImportError
            ? new ItineraryImportError({
                code: err.code,
                message: userMessageForItineraryImportCode(err.code, err.message),
                retryable: err.retryable,
                httpStatus: err.httpStatus,
              })
            : new ItineraryImportError({
                code: 'UPSTREAM_FAILED',
                message: userMessageForItineraryImportCode('UPSTREAM_FAILED'),
                retryable: true,
                httpStatus: 503,
              });
        logError('[TripImportFlow] parse failed', {
          code: importErr.code,
          import_id: params.session.importId,
          source_type: params.sourceType,
        });
        WebAnalytics.track(EVENT_TRIP_IMPORT_PARSE_FAILED, {
          entry,
          source_type: params.sourceType,
          import_id: params.session.importId,
          failure_code: importErr.code,
          retryable: importErr.retryable,
          http_status: importErr.httpStatus,
        });
        setImportError(importErr);
        setViewState('error');
      }
    },
    [applyDraft, entry, socialOnly],
  );

  useEffect(() => {
    if (viewState !== 'parsing' && viewState !== 'uploading') {
      progressStartedRef.current = null;
      return undefined;
    }
    const interval = window.setInterval(() => {
      if (progressFrozenRef.current) return;
      const started = progressStartedRef.current;
      if (started == null) return;
      const elapsed = Date.now() - started;
      setProgressState((previous) =>
        tripImportProgressReducer(previous, elapsed, progressCopyRef.current),
      );
    }, 120);
    return () => {
      window.clearInterval(interval);
    };
  }, [viewState]);

  const handleCancelImport = useCallback(() => {
    importAbortRef.current?.abort();
    importAbortRef.current = null;
    WebAnalytics.track(EVENT_TRIP_IMPORT_FLOW_CANCELLED, {
      entry,
      import_id: importId || null,
      ...(isOnboardingEmbed ? { source: 'onboarding_embed' } : {}),
    });
    setViewState('idle');
    setProgressFoundPlaces(null);
  }, [entry, importId, isOnboardingEmbed]);

  const handlePublishAndShare = useCallback(async () => {
    if (!savedTripId) return;
    WebAnalytics.track(EVENT_TRIP_IMPORT_PUBLISH_CLICKED, {
      entry,
      import_id: importId,
      post_id: savedTripId,
    });
    setIsPublishing(true);
    importShareModalOpenRef.current = false;
    try {
      await updatePostPrivacy(savedTripId, false);
      WebAnalytics.track(EVENT_TRIP_IMPORT_PUBLISHED, {
        entry,
        import_id: importId,
        post_id: savedTripId,
      });
      await handleShareClick(savedTripId, savedTripTitle, 'Itinerary');
      if (!importShareModalOpenRef.current) {
        exitImportToSavedTrip();
      }
    } catch (err) {
      WebAnalytics.track(EVENT_TRIP_IMPORT_PUBLISH_FAILED, {
        entry,
        import_id: importId,
        post_id: savedTripId,
        reason: getErrorMessage(err),
      });
      toast.error(getErrorMessage(err) || 'Could not publish.');
    } finally {
      setIsPublishing(false);
    }
  }, [entry, exitImportToSavedTrip, handleShareClick, importId, savedTripId, savedTripTitle]);

  const handlePasteImport = useCallback(async () => {
    const trimmed = inputText.trim();
    if (!trimmed) {
      toast.error('Paste your itinerary or list of places first.');
      return;
    }
    if (trimmed.length > IMPORT_TEXT_MAX_CHARS) {
      toast.error(`Text is too long. Maximum is ${IMPORT_TEXT_MAX_CHARS.toLocaleString()} characters.`);
      return;
    }
    const session = createImportSession();
    setImportId(session.importId);
    setImportStartedAt(session.importStartedAt);
    await runParse({
      source: { type: 'text', text: trimmed },
      sourceType: 'text',
      session,
    });
  }, [inputText, runParse]);

  const handleImportFile = useCallback(
    async (file: File) => {
      const session = createImportSession();
      setImportId(session.importId);
      setImportStartedAt(session.importStartedAt);
      setProgressState(initialTripImportProgressState());
      setProgressFoundPlaces(null);
      progressStartedRef.current = Date.now();
      setViewState('uploading');
      WebAnalytics.track(EVENT_TRIP_IMPORT_UPLOAD_STARTED, {
        entry,
        import_id: session.importId,
      });
      try {
        const { key, sourceType: docType } = await clientUploadImportDocument(file);
        await runParse({
          source: { type: docType, key },
          sourceType: docType,
          session,
          preserveProgressTimer: true,
        });
      } catch (err) {
        WebAnalytics.track(EVENT_TRIP_IMPORT_UPLOAD_FAILED, {
          entry,
          import_id: session.importId,
          reason: getErrorMessage(err),
        });
        toast.error(getErrorMessage(err));
        setViewState('idle');
      }
    },
    [entry, runParse],
  );

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      await handleImportFile(file);
    },
    [handleImportFile],
  );

  const handleTogglePlace = useCallback(
    (clientId: string, matchConfidence: string, category: string | null | undefined) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(clientId)) {
          next.delete(clientId);
        } else {
          next.add(clientId);
        }
        return next;
      });
      WebAnalytics.track(EVENT_TRIP_IMPORT_PLACE_TOGGLED, {
        entry,
        import_id: importId,
        match_confidence: matchConfidence,
        category: category ?? null,
        result_type: resultType,
      });
    },
    [entry, importId, resultType],
  );

  const handleResultTypeChange = useCallback(
    (next: TripImportResultType) => {
      if (next === resultType) return;
      WebAnalytics.track(EVENT_TRIP_IMPORT_RESULT_TYPE_SWITCHED, {
        entry,
        import_id: importId,
        from: resultType,
        to: next,
        suggested_result_type: suggestedType,
      });
      setResultType(next);
    },
    [entry, importId, resultType, suggestedType],
  );

  const finishClose = useCallback(() => {
    setShowDiscardConfirm(false);
    WebAnalytics.track(EVENT_TRIP_IMPORT_FLOW_CANCELLED, {
      entry,
      import_id: importId || null,
      ...(isOnboardingEmbed ? { source: 'onboarding_embed' } : {}),
    });
    if (isOnboardingEmbed) {
      onCancel?.();
      return;
    }
    clearPrefilledText?.();
    onRequestClose?.();
  }, [clearPrefilledText, entry, importId, isOnboardingEmbed, onCancel, onRequestClose]);

  const handleClose = useCallback(() => {
    if (viewState === 'review' || viewState === 'pick_destination') {
      setShowDiscardConfirm(true);
      return;
    }
    finishClose();
  }, [finishClose, viewState]);

  const discardConfirmDialog = (
    <TripImportDiscardConfirmDialog
      open={showDiscardConfirm}
      onKeepEditing={() => setShowDiscardConfirm(false)}
      onDiscard={finishClose}
    />
  );

  const listPlaces = useMemo(() => {
    if (!draft) return [];
    return flattenDraftToListPlaces(draft, selectedIds, sourceType);
  }, [draft, selectedIds, sourceType]);

  useEffect(() => {
    if (!autoStartImport && !isOnboardingEmbed) return;
    const trimmed = prefilledText.trim();
    if (!trimmed) return;
    if (processedTextRef.current === trimmed) return;
    processedTextRef.current = trimmed;
    const session = createImportSession();
    setImportId(session.importId);
    setImportStartedAt(session.importStartedAt);
    void runParse({
      source: { type: 'text', text: trimmed },
      sourceType: 'text',
      session,
    });
  }, [autoStartImport, isOnboardingEmbed, prefilledText, runParse]);

  const handleSocialImportFromUrl = useCallback(
    (url: string) => {
      const source = buildSocialItineraryImportSource(url);
      if (!source) {
        setParseErrorMessage('Paste a valid Instagram or TikTok link.');
        return;
      }
      onSocialUrlAccepted?.(url);
      const session = createImportSession();
      setImportId(session.importId);
      setImportStartedAt(session.importStartedAt);
      void runParse({
        source,
        sourceType: source.type,
        session,
      });
    },
    [onSocialUrlAccepted, runParse],
  );

  useEffect(() => {
    const trimmed = prefilledSocialUrl.trim();
    if (!trimmed) return;
    if (socialOnly && !autoStartImport) return;
    if (processedSocialUrlRef.current === trimmed) return;
    processedSocialUrlRef.current = trimmed;
    const source = buildSocialItineraryImportSource(trimmed);
    if (!source) return;
    const session = createImportSession();
    setImportId(session.importId);
    setImportStartedAt(session.importStartedAt);
    void runParse({
      source,
      sourceType: source.type,
      session,
    });
  }, [autoStartImport, prefilledSocialUrl, runParse, socialOnly]);

  useEffect(() => {
    if (viewState !== 'save_success' || !onCompleteSave) return;

    if (savedTripId) {
      onCompleteSave({
        destination: {
          type: 'itinerary',
          id: savedTripId,
          name: savedTripTitle,
          isNew: true,
        },
        savedCount: selectedIds.size,
        selectedCount: selectedIds.size,
        previewPlace: listPlaces[0] ?? null,
      });
      return;
    }

    if (saveSummary && saveSummary.destination.type !== 'saves') {
      const dest = saveSummary.destination as SocialImportDestination;
      onCompleteSave({
        destination: {
          type: dest.type,
          id: dest.id,
          name: dest.name,
          isNew: dest.isNew,
        },
        savedCount: saveSummary.savedCount,
        selectedCount: listPlaces.length,
        previewPlace: listPlaces[0] ?? null,
      });
    }
  }, [
    listPlaces,
    onCompleteSave,
    saveSummary,
    savedTripId,
    savedTripTitle,
    selectedIds.size,
    viewState,
  ]);

  const fetchSaveDestinations = useCallback(async () => {
    if (!user) {
      setUserItineraries([]);
      setUserLists([]);
      return;
    }
    setIsLoadingItineraries(true);
    setIsLoadingLists(true);
    try {
      const [itinerariesResponse, listsResponse] = await Promise.all([
        getUserItineraries({ page: 1, limit: 50, includeDrafts: false, includePrivate: false }),
        getUserLists({ page: 1, limit: 50, includeDrafts: false, includePrivate: false }),
      ]);
      setUserItineraries(itinerariesResponse.posts);
      setUserLists(listsResponse.posts);
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Could not load trips and lists.');
    } finally {
      setIsLoadingItineraries(false);
      setIsLoadingLists(false);
    }
  }, [user]);

  const savingTitle =
    savingKind === 'trip'
      ? 'Creating your trip…'
      : savingKind === 'saves'
        ? 'Saving to Saves…'
        : 'Saving your places…';

  const handleCreateTrip = useCallback(async () => {
    if (!draft || selectedIds.size === 0) return;
    setSavingKind('trip');
    setViewState('saving');
    WebAnalytics.track(EVENT_TRIP_IMPORT_CREATE_STARTED, {
      entry,
      import_id: importId,
      result_type: 'itinerary',
      destination_type: 'itinerary',
    });
    try {
      const created = await createItineraryFromImportDraft({
        draft,
        selectedClientIds: selectedIds,
        sourceType,
        importId,
        importStartedAt,
        tripTitle: importTitle,
      });
      WebAnalytics.track(EVENT_TRIP_IMPORT_CREATE_SUCCEEDED, {
        entry,
        import_id: importId,
        result_type: 'itinerary',
        destination_type: 'itinerary',
        post_id: created._id,
        saved_place_count: selectedIds.size,
        seconds_since_import_start: Math.round(
          (Date.now() - Date.parse(importStartedAt)) / 1000,
        ),
      });
      setSavedTripId(created._id);
      setSavedTripTitle(created.name ?? draft.title);
      setViewState('save_success');
      onSaveComplete?.();
    } catch (err) {
      WebAnalytics.track(EVENT_TRIP_IMPORT_CREATE_FAILED, {
        entry,
        import_id: importId,
        result_type: 'itinerary',
        reason: getErrorMessage(err),
      });
      toast.error(getErrorMessage(err));
      setViewState('review');
    }
  }, [draft, entry, importId, importStartedAt, importTitle, onSaveComplete, selectedIds, sourceType]);

  const handleBulkSaveToSaves = useCallback(async () => {
    if (!draft || listPlaces.length === 0) {
      toast.error('Select at least one place to save.');
      return;
    }
    setSavingKind('saves');
    setViewState('saving');
    WebAnalytics.track(EVENT_TRIP_IMPORT_CREATE_STARTED, {
      entry,
      import_id: importId,
      result_type: 'list',
      destination_type: 'saves',
    });
    try {
      const socialPostId = mongoObjectIdStringOrNull(draft.social?.post_id);
      const result = await bulkSaveSocialImportPlaces(listPlaces, {
        sourceSocialMediaPostId: socialPostId,
      });
      if (result.savedCount === 0) {
        throw new Error('Could not save any places.');
      }
      const dominantCountry = pickDominantCountry(listPlaces);
      const dominantState = pickDominantState(listPlaces);
      dispatchSavedContentRefresh({
        country: dominantCountry,
        savedCount: result.savedCount,
        source: 'trip_import',
      });
      WebAnalytics.track(EVENT_TRIP_IMPORT_CREATE_SUCCEEDED, {
        entry,
        import_id: importId,
        result_type: 'list',
        destination_type: 'saves',
        post_id: null,
        saved_place_count: result.savedCount,
        seconds_since_import_start: Math.round(
          (Date.now() - Date.parse(importStartedAt)) / 1000,
        ),
      });
      if (dominantCountry && isModal) {
        let albumRoute: string;
        if (dominantCountry === US_COUNTRY_NAME && dominantState) {
          albumRoute = `/saved/united-states/${stateToRouteParam(dominantState)}`;
        } else {
          albumRoute = `/saved/${countryToRouteParam(dominantCountry)}`;
        }
        onRequestClose?.();
        navigate(albumRoute, {
          state: {
            postSaveOverlay: {
              countryName: dominantCountry,
              stateName: dominantState,
              savedCount: result.savedCount,
              coverImageUrl: listPlaces[0]?.imageUrl ?? null,
            },
          },
        });
        onSaveComplete?.();
        return;
      }
      setSaveSummary({
        destination: { type: 'saves', name: dominantCountry, stateName: dominantState },
        savedCount: result.savedCount,
      });
      setViewState('save_success');
      onSaveComplete?.();
    } catch (err) {
      WebAnalytics.track(EVENT_TRIP_IMPORT_CREATE_FAILED, {
        entry,
        import_id: importId,
        result_type: 'list',
        reason: getErrorMessage(err),
      });
      toast.error(getErrorMessage(err));
      setViewState('review');
    }
  }, [
    draft,
    entry,
    importId,
    importStartedAt,
    isModal,
    listPlaces,
    navigate,
    onRequestClose,
    onSaveComplete,
  ]);

  const handleListDestination = useCallback(
    async (selection: SaveDestinationSelection) => {
      if (!draft) return;
      const places = listPlaces;
      if (places.length === 0) {
        toast.error('Select at least one place to save.');
        setViewState('review');
        return;
      }
      setSavingKind('list_destination');
      setViewState('saving');
      WebAnalytics.track(EVENT_TRIP_IMPORT_CREATE_STARTED, {
        entry,
        import_id: importId,
        result_type: 'list',
        destination_type: selection.destinationType,
      });
      WebAnalytics.track(EVENT_TRIP_IMPORT_DESTINATION_SELECTED, {
        entry,
        import_id: importId,
        destination_type: selection.destinationType,
        destination_is_new: selection.isNew,
      });
      try {
        const destination: SocialImportDestination = {
          type: selection.destinationType,
          id: selection.destinationId,
          name: selection.destinationName,
          isNew: selection.isNew,
          seedPlaceId: selection.seedPlaceId,
        };

        if (selection.isNew && selection.destinationType === 'list' && places[0]) {
          const sourceMetadata = buildImportSourceMetadata(
            draft,
            sourceType,
            importId,
            importStartedAt,
          );
          const listName =
            selection.destinationName?.trim() || importTitle.trim() || draft.title?.trim() || 'New List';
          const listNotes = importListNotesFromDraft(draft, sourceType);
          const socialPostId = mongoObjectIdStringOrNull(draft.social?.post_id);
          const { primaryDestination, cityIdentifyingNames } =
            listCreateDestinationOptionsFromDraft(draft);
          const created = await createListWithPlace(places[0].placeCardData, listName, {
            sourceMetadata,
            ...(listNotes ? { listNotes } : {}),
            ...(primaryDestination ? { primaryDestination } : {}),
            ...(cityIdentifyingNames.length > 0 ? { cityIdentifyingNames } : {}),
            ...(socialPostId ? { sourceSocialMediaPostId: socialPostId } : {}),
          });
          destination.id = created._id;
          destination.seedPlaceId = places[0].id;
        }

        const skipAppendPostFields =
          selection.isNew && selection.destinationType === 'list';
        const socialPostId = mongoObjectIdStringOrNull(draft.social?.post_id);
        const appendOptions = skipAppendPostFields
          ? undefined
          : {
              ...buildImportAppendToExistingOptions({
                draft,
                sourceType,
                importId,
                importStartedAt,
              }),
              ...(socialPostId ? { sourceSocialMediaPostId: socialPostId } : {}),
            };
        const result = await addSocialImportPlacesToDestination(
          destination,
          places,
          appendOptions,
        );
        // Places were added on the server outside the trip editor's save.
        invalidateExistingPostCache(destination.id);
        WebAnalytics.track(EVENT_TRIP_IMPORT_CREATE_SUCCEEDED, {
          entry,
          import_id: importId,
          result_type: 'list',
          destination_type: selection.destinationType,
          post_id: destination.id,
          saved_place_count: result.savedCount,
          seconds_since_import_start: Math.round(
            (Date.now() - Date.parse(importStartedAt)) / 1000,
          ),
        });
        setSaveSummary({
          destination,
          savedCount: result.savedCount,
        });
        setViewState('save_success');
        onSaveComplete?.();
      } catch (err) {
        WebAnalytics.track(EVENT_TRIP_IMPORT_CREATE_FAILED, {
          entry,
          import_id: importId,
          result_type: 'list',
          reason: getErrorMessage(err),
        });
        toast.error(getErrorMessage(err));
        setViewState('pick_destination');
      }
    },
    [draft, entry, importId, importStartedAt, importTitle, listPlaces, sourceType],
  );

  const handlePickItinerary = useCallback(
    async (itinerary: SavedItinerary) => {
      await handleListDestination({
        destinationType: 'itinerary',
        destinationId: itinerary.id,
        destinationName: itinerary.name,
        isNew: false,
      });
    },
    [handleListDestination],
  );

  const handlePickList = useCallback(
    async (list: SavedItinerary) => {
      await handleListDestination({
        destinationType: 'list',
        destinationId: list.id,
        destinationName: list.name,
        isNew: false,
      });
    },
    [handleListDestination],
  );

  const handleCreateNewListFromPicker = useCallback(
    async (listTitle: string) => {
      if (!listPlaces[0]) return;
      await handleListDestination({
        destinationType: 'list',
        destinationId: '',
        destinationName: listTitle,
        isNew: true,
        seedPlaceId: listPlaces[0].id,
      });
    },
    [handleListDestination, listPlaces],
  );

  const handleCreateListFromReview = useCallback(() => {
    if (listPlaces.length === 0) {
      toast.error('Select at least one place to continue.');
      return;
    }
    handleResultTypeChange('list');
    const listTitle = importTitle.trim() || draft?.title?.trim() || 'New List';
    void handleCreateNewListFromPicker(listTitle);
  }, [draft?.title, handleCreateNewListFromPicker, handleResultTypeChange, importTitle, listPlaces.length]);

  const handleOpenSavePicker = useCallback(() => {
    if (listPlaces.length === 0) {
      toast.error('Select at least one place to continue.');
      return;
    }
    WebAnalytics.track(EVENT_TRIP_IMPORT_ADD_TO_EXISTING_CLICKED, {
      entry,
      import_id: importId,
      selected_place_count: selectedIds.size,
      source_type: sourceType,
    });
    void fetchSaveDestinations();
    setViewState('pick_destination');
  }, [entry, fetchSaveDestinations, importId, listPlaces.length, selectedIds.size, sourceType]);

  const stats = useMemo(() => {
    if (!draft) return null;
    return summarizeImport(draft, selectedIds);
  }, [draft, selectedIds]);

  const redactionLine = draft ? formatRedactionSummary(draft) : null;

  const lookupPlace = useMemo(() => {
    if (!draft || !placeLookupClientId) return null;
    for (const day of draft.days) {
      const found = day.places.find((place) => place.client_id === placeLookupClientId);
      if (found) return found;
    }
    return null;
  }, [draft, placeLookupClientId]);

  const lookupCity = useMemo(
    () => (draft ? resolveImportDraftDestination(draft).city : null),
    [draft],
  );

  const handleMatchImportPlace = useCallback(
    (location: Location) => {
      if (!draft || !lookupPlace) return;
      const nextPlace = applyGoogleMatchToImportPlace(lookupPlace, location);
      if (!nextPlace) return;
      setDraft({
        ...draft,
        days: draft.days.map((day) => ({
          ...day,
          places: day.places.map((place) =>
            place.client_id === nextPlace.client_id ? nextPlace : place,
          ),
        })),
      });
      WebAnalytics.track(EVENT_TRIP_IMPORT_PLACE_MATCHED, {
        import_id: importId,
        client_id: nextPlace.client_id,
        category: nextPlace.category ?? 'do',
      });
      WebAnalytics.track(EVENT_CUSTOM_PLACE_UPGRADED, {
        post_id: null,
        entry: 'import_review',
      });
      setPlaceLookupClientId(null);
    },
    [draft, importId, lookupPlace],
  );

  const showBookableEarnings = isCreatorOrAdvisor(user);

  const reviewSections = useMemo(() => {
    if (!draft) return [];
    const built = buildTripImportReviewSections(draft, resultType, {
      showDetailed: reviewDetailed,
      showBookableEarnings,
      notesInExpandedPanel: true,
    });
    return filterTripImportReviewSections(built, draft, reviewFilter);
  }, [draft, resultType, reviewDetailed, reviewFilter, showBookableEarnings]);

  const totalPlaceCount = draft ? countDraftPlacesTotal(draft) : 0;

  const importPreviewLabel =
    sourceType === 'pdf'
      ? 'Imported from PDF'
      : sourceType === 'docx'
        ? 'Imported from Word'
        : 'Imported from your notes';

  const importMetaLine = draft
    ? formatTripImportReviewMetaLine(importPreviewLabel, draft)
    : importPreviewLabel;

  const socialSourcePreview = useMemo(() => {
    const displayUrl = draft?.social?.display_url;
    if (!displayUrl || typeof displayUrl !== 'string') return null;
    const platform = draft?.social?.platform === 'tiktok' ? 'tiktok' : 'instagram';
    return { imageUrl: displayUrl, platform } as const;
  }, [draft]);

  const isSocialImportFlow =
    socialOnly || isSocialItineraryImportSourceType(sourceType);

  const handleReviewTogglePlace = useCallback(
    (clientId: string) => {
      if (!draft) return;
      for (const day of draft.days) {
        const place = day.places.find((p) => p.client_id === clientId);
        if (place) {
          handleTogglePlace(
            clientId,
            place.match_confidence,
            place.category ?? place.location?.gt_category,
          );
          return;
        }
      }
    },
    [draft, handleTogglePlace],
  );

  const handlePlaceNotesChange = useCallback((clientId: string, notes: string) => {
    setDraft((previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        days: previous.days.map((day) => ({
          ...day,
          places: day.places.map((place) =>
            place.client_id === clientId ? { ...place, notes } : place,
          ),
        })),
      };
    });
  }, []);

  const handlePlaceDetailsExpandedChange = useCallback(
    (clientId: string, expanded: boolean) => {
      WebAnalytics.track(EVENT_TRIP_IMPORT_PLACE_DETAILS_TOGGLED, {
        entry,
        import_id: importId,
        client_id: clientId,
        expanded,
      });
    },
    [entry, importId],
  );

  const isModalVariant = isModal && variant !== 'onboarding';
  const isSocialIdle = socialOnly && viewState === 'idle';
  const isIdleModalInput = viewState === 'idle' && isModalVariant && !socialOnly;
  const isProgressModal =
    (isModalVariant || socialOnly) && (viewState === 'uploading' || viewState === 'parsing');
  const isReviewModal =
    (isModalVariant || socialOnly) && viewState === 'review';
  const isSuccessModal =
    (isModalVariant || socialOnly) &&
    viewState === 'save_success' &&
    Boolean(savedTripId);
  const isCompactModalShell =
    isSocialIdle ||
    isIdleModalInput ||
    isProgressModal ||
    isReviewModal ||
    isSuccessModal;
  const panelPadding = isCompactModalShell ? '' : 'p-5 min-[768px]:p-6';

  const panel = (
    <section
      className={
        isCompactModalShell
          ? `flex w-full flex-col overflow-hidden bg-white shadow-[0_0_20px_rgba(0,0,0,0.15)] ${
              isMobileViewport
                ? 'max-h-[min(90dvh,calc(100dvh-env(safe-area-inset-top)-24px))] min-h-0 rounded-t-[20px]'
                : 'shrink-0 rounded-[20px]'
            }`
          : `flex h-import-modal-panel max-h-import-modal-panel w-full flex-col overflow-hidden rounded-[20px] bg-white shadow-[0_0_20px_rgba(0,0,0,0.15)] ${panelPadding}`
      }
    >
      {viewState === 'idle' && isModalVariant && !socialOnly ? (
        <TripImportInputStep
          variant="modal"
          inputText={inputText}
          onInputTextChange={setInputText}
          parseErrorMessage={parseErrorMessage}
          isBusy={isBusy}
          fileInputRef={fileInputRef}
          onFileChange={(event) => {
            void handleFileChange(event);
          }}
          outputFormatPreference={outputFormatPreference}
          onOutputFormatChange={setOutputFormatPreference}
          onClose={() => void handleClose()}
          onImportFile={(file) => {
            void handleImportFile(file);
          }}
          onImportText={() => {
            void handlePasteImport();
          }}
          onUploadClick={() => fileInputRef.current?.click()}
        />
      ) : null}

      {isSocialIdle ? (
        <TripImportSocialInputStep
          prefilledUrl={prefilledSocialUrl}
          entrySource={entrySource}
          isOnboardingEmbed={isOnboardingEmbed}
          parseErrorMessage={parseErrorMessage}
          autoStartFromPrefilled={false}
          onClose={() => {
            if (isOnboardingEmbed) {
              onCancel?.();
              return;
            }
            void handleClose();
          }}
          onImport={handleSocialImportFromUrl}
        />
      ) : null}

      {viewState === 'idle' && !isModalVariant && !socialOnly ? (
        <TripImportInputStep
          variant="embedded"
          inputText={inputText}
          onInputTextChange={setInputText}
          parseErrorMessage={parseErrorMessage}
          isBusy={isBusy}
          fileInputRef={fileInputRef}
          onFileChange={(event) => {
            void handleFileChange(event);
          }}
          outputFormatPreference={outputFormatPreference}
          onOutputFormatChange={setOutputFormatPreference}
          onImportText={() => {
            void handlePasteImport();
          }}
          onUploadClick={() => fileInputRef.current?.click()}
        />
      ) : null}

      {viewState === 'uploading' || viewState === 'parsing' ? (
        <TripImportProgressStep
          progress={progressState}
          isUploading={viewState === 'uploading'}
          foundPlaces={progressFoundPlaces}
          visual={isSocialImportFlow ? 'socialScan' : 'checklist'}
          onCancel={handleCancelImport}
        />
      ) : null}

      {viewState === 'saving' ? (
        <div
          className="flex min-h-[280px] flex-1 flex-col items-center justify-center gap-4 px-4 py-8"
          aria-live="polite"
          aria-busy="true"
        >
          <span
            className="inline-flex h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-orange-500"
            aria-hidden="true"
          />
          <div className="flex max-w-sm flex-col items-center gap-2 text-center">
            <h2 className="font-lora text-lg font-semibold text-gray-900">{savingTitle}</h2>
            <p className="font-manrope text-sm text-gray-600">
              {savingKind === 'trip'
                ? 'We’re building days and places from your import.'
                : 'This usually takes a few seconds.'}
            </p>
          </div>
        </div>
      ) : null}

      {viewState === 'review' && draft && stats ? (
        <>
        <div
          className={`flex min-h-0 flex-1 flex-col min-[1140px]:flex-row ${
            socialSourcePreview ? 'trip-import-review-with-preview' : ''
          }`}
        >
          <TripImportReviewStep
            entry={entry}
            importId={importId}
            draft={draft}
            redactionLine={redactionLine}
            resultType={resultType}
            suggestedType={suggestedType}
            suggestionReason={suggestionReason}
            reviewFilter={reviewFilter}
            onReviewFilterChange={setReviewFilter}
            reviewDetailed={reviewDetailed}
            onReviewDetailedChange={setReviewDetailed}
            importMetaLine={importMetaLine}
            importTitle={importTitle}
            onImportTitleChange={setImportTitle}
            totalPlaceCount={totalPlaceCount}
            reviewSections={reviewSections}
            selectedIds={selectedIds}
            onTogglePlace={handleReviewTogglePlace}
            isBusy={isBusy}
            onClose={() => void handleClose()}
            onReRunImport={() => {
              const last = lastParseRef.current;
              if (!last) return;
              void runParse({ ...last, force: true });
            }}
            onCreateTrip={() => {
              void handleCreateTrip();
            }}
            onCreateList={handleCreateListFromReview}
            sourceType={sourceType}
            reviewIntroLine={
              isSocialImportFlow && draft
                ? formatSocialImportReviewSubtitle(
                    totalPlaceCount,
                    draft.social?.platform === 'tiktok'
                      ? 'tiktok'
                      : draft.social?.platform === 'instagram'
                        ? 'instagram'
                        : null,
                  )
                : null
            }
            footerVariant={isSocialImportFlow ? 'social' : 'default'}
            onAddToExisting={isSocialImportFlow ? handleOpenSavePicker : undefined}
            onSaveToMyTravel={
              isSocialItineraryImportSourceType(sourceType)
                ? () => {
                    void handleBulkSaveToSaves();
                  }
                : undefined
            }
            onFindPlace={(clientId) => setPlaceLookupClientId(clientId)}
            onPlaceNotesChange={handlePlaceNotesChange}
            onPlaceDetailsExpandedChange={handlePlaceDetailsExpandedChange}
          />
          {socialSourcePreview ? (
            <TripImportSourcePreviewPanel
              imageUrl={socialSourcePreview.imageUrl}
              platform={socialSourcePreview.platform}
            />
          ) : null}
        </div>
        {lookupPlace && lookupPlace.row_kind !== 'travel' ? (
          <PlaceSearchAutocomplete
            isOpen
            variant="lookup"
            sheetTitle="Find this place"
            initialSearchQuery={lookupPlace.name}
            onClose={() => setPlaceLookupClientId(null)}
            onAddPlace={(location) => handleMatchImportPlace(location)}
            dayIndex={0}
            city={lookupCity}
            days={[]}
            postType={resultType === 'list' ? 'List' : 'Itinerary'}
          />
        ) : null}
        </>
      ) : null}

      {viewState === 'pick_destination' && draft ? (
        <SaveDestinationPickerPanel
          variant="inline"
          active={viewState === 'pick_destination'}
          onBack={() => setViewState('review')}
          savedItineraries={userItineraries}
          savedLists={userLists}
          isLoading={isLoadingItineraries}
          isLoadingLists={isLoadingLists}
          place={listPlaces[0]?.placeCardData ?? null}
          onPickItinerary={handlePickItinerary}
          onPickList={handlePickList}
          onCreateNewItinerary={async () => {
            toast.error('Create a trip from Trip mode, then add places there.');
          }}
          onCreateNewList={handleCreateNewListFromPicker}
          suggestedNewListTitle={importTitle.trim() || draft.title?.trim()}
          suggestedNewItineraryTitle={importTitle.trim() || draft.title?.trim()}
        />
      ) : null}

      {viewState === 'save_success' ? (
        savedTripId ? (
          <TripImportSuccessStep
            draft={draft}
            savedTripId={savedTripId}
            savedTripTitle={savedTripTitle}
            selectedClientIds={selectedIds}
            isPublishing={isPublishing}
            showPublishEarningsCopy={showBookableEarnings}
            onClose={() => {
              onRequestClose?.();
            }}
            onKeepEditing={() => {
              WebAnalytics.track(EVENT_TRIP_IMPORT_KEEP_EDITING_CLICKED, {
                entry,
                import_id: importId,
                post_id: savedTripId,
              });
              exitImportToSavedTrip();
            }}
            onPublishAndShare={() => {
              void handlePublishAndShare();
            }}
          />
        ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
          <h2 className="font-lora text-xl font-semibold text-gray-900">Saved</h2>
          {saveSummary ? (
            <>
              <p className="font-manrope text-sm text-gray-600">
                Saved {saveSummary.savedCount} places
                {saveSummary.destination.type !== 'saves'
                  ? ` to ${saveSummary.destination.name}`
                  : saveSummary.destination.name
                    ? ` to ${saveSummary.destination.name}`
                    : ''}
              </p>
              {saveSummary.destination.type === 'list' &&
              (saveSummary.destination as SocialImportDestination).id ? (
                <button
                  type="button"
                  className="rounded-button bg-orange-500 px-4 py-3 font-manrope text-base text-white"
                  onClick={() => {
                    const dest = saveSummary.destination as SocialImportDestination;
                    WebAnalytics.track(EVENT_TRIP_IMPORT_VIEW_LIST_CLICKED, {
                      entry,
                      import_id: importId,
                      post_id: dest.id,
                      destination_is_new: dest.isNew,
                    });
                    onRequestClose?.();
                    navigate(`/list/${dest.id}`);
                  }}
                >
                  View list
                </button>
              ) : null}
              {saveSummary.destination.type === 'itinerary' &&
              (saveSummary.destination as SocialImportDestination).id ? (
                <button
                  type="button"
                  className="rounded-button bg-orange-500 px-4 py-3 font-manrope text-base text-white"
                  onClick={() => {
                    const dest = saveSummary.destination as SocialImportDestination;
                    WebAnalytics.track(EVENT_TRIP_IMPORT_VIEW_TRIP_CLICKED, {
                      entry,
                      import_id: importId,
                      post_id: dest.id,
                      destination_is_new: dest.isNew,
                    });
                    onRequestClose?.();
                    navigate(`/itinerary/${dest.id}`);
                  }}
                >
                  View trip
                </button>
              ) : null}
            </>
          ) : null}
        </div>
        )
      ) : null}

      {viewState === 'error' && importError ? (
        <div role="alert" className="flex flex-1 flex-col gap-4">
          <h2 className="font-lora text-xl font-semibold text-gray-900">Import failed</h2>
          <p className="font-manrope text-sm text-gray-700">{importError.message}</p>
          {importError.code === 'INSUFFICIENT_TEXT' &&
          (sourceType === 'pdf' || sourceType === 'docx') ? (
            <p className="font-manrope text-sm text-gray-600">
              Try pasting the text from your document instead, or save as .docx / PDF and upload again.
            </p>
          ) : null}
          {importError.code === 'INVALID_FILE' ? (
            <p className="font-manrope text-sm text-gray-600">
              PDFs are limited to {IMPORT_PDF_MAX_PAGES} pages and 20 MB. Word files must be valid .docx.
            </p>
          ) : null}
          {importError.code === 'RATE_LIMITED' ? (
            <p className="font-manrope text-sm text-gray-600">
              You can run up to 30 new imports per hour. Re-importing the same content may use the cache
              and won&apos;t count toward that limit. Try again later.
            </p>
          ) : null}
          {importError.code !== 'RATE_LIMITED' && importError.retryable ? (
            <button
              type="button"
              className="rounded-sheet bg-brand-orange px-4 py-3 font-manrope text-base text-white"
              onClick={() => setViewState('idle')}
            >
              Try again
            </button>
          ) : null}
        </div>
      ) : null}

      {viewState !== 'saving' && !isCompactModalShell ? (
        <div className="mt-4 flex shrink-0 justify-end px-1 pb-1">
          <button
            type="button"
            className="font-manrope text-sm font-semibold text-[#525257] hover:text-[#252528]"
            onClick={() => void handleClose()}
          >
            Close
          </button>
        </div>
      ) : null}
    </section>
  );

  if (variant === 'onboarding') {
    return (
      <>
        {panel}
        {discardConfirmDialog}
        {shareModal}
      </>
    );
  }

  if (!isModal) {
    return (
      <>
        {panel}
        {discardConfirmDialog}
        {shareModal}
      </>
    );
  }

  return (
    <>
      <ResponsiveOverlay
        onClose={handleClose}
        presentation="sheet"
        className="trip-import-overlay !z-import-modal"
        aria-label={socialOnly ? 'Import from social media' : 'Import your travel plans'}
        showSheetHandle={false}
        panelClassName={
          isMobileViewport ? 'trip-import-mobile-sheet' : 'trip-import-desktop-dialog'
        }
      >
        <div onClick={(e) => e.stopPropagation()} className="w-full">
          {panel}
        </div>
      </ResponsiveOverlay>
      {discardConfirmDialog}
      {shareModal}
    </>
  );
}
